import { useRef, useState } from 'react';

import { Button, FormValidationError, Label, TextInput, Title } from '@ce-cfbdigital/cgl-cfb-fe-foundation';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import CustomIcon from '../../../common/CustomIcons';
import FileUpload from '../../../common/FileUpload';

import './styles/index.css';

const FormRequestInfoPage = props => {
  const {
    onSubmit,
    labels: {
      browseFile,
      dragFile,
      orLabel,
      requirementMessageOne,
      requirementMessageTwo,
      facility,
      requestDescription,
      detailOfRequest,
      technicalDocSupport,
      useFormToRequest,
      cancelLabel,
      sendLabel,
      messagePlaceHolder
    },
    handleCloseModal,
    onFileSelect,
    acceptedExtensions,
    selectedFiles,
    onRemoveFile
  } = props;

  const hiddenInnerSubmitFormRef = useRef(null);

  const [formState, setFormState] = useState({
    facility: '',
    requestDescription: '',
    detailOfRequest: ''
  });

  const updateFormState = state => {
    setFormState(state);
  };

  const createValidationSchema = () => {
    const {
      messages: {
        requiredFacilityMessage,
        requiredRequestDetailMessage,
        requiredCommentMessage,
        specialCharErrorMessage
      }
    } = props;

    return Yup.object().shape({
      facility: Yup.string()
        .required(requiredFacilityMessage)
        .matches(/^[^:]*$/, specialCharErrorMessage),
      requestDescription: Yup.string()
        .required(requiredRequestDetailMessage)
        .matches(/^[^:]*$/, specialCharErrorMessage),
      detailOfRequest: Yup.string()
        .required(requiredCommentMessage)
        .matches(/^[^:]*$/, specialCharErrorMessage)
    });
  };

  const getTextInput = (title, values, errors, setFieldValue, type, inputType = 'text') => (
    <>
      <Label className="request-doc-input-label b required" htmlFor={type}>
        {title}
      </Label>
      <TextInput
        value={values[type]}
        onChange={e => setFieldValue(type, e.target.value, false)}
        id={type}
        inputClassName={`request-doc-input-field ${errors[type] ? 'error' : ''}`}
        type={inputType}
      />
      {errors[type] && <FormValidationError errorMessage={errors[type]} />}
    </>
  );

  const handleChange = setFieldValue => event => {
    const target = event.currentTarget;
    const { value } = target;
    const id = target.getAttribute('id');
    setFieldValue(id, value, false);
  };

  const handleSend = () => {
    hiddenInnerSubmitFormRef.current.click();
  };

  return (
    <Formik initialValues={formState} validationSchema={createValidationSchema()} onSubmit={values => onSubmit(values)}>
      {({ values, errors, handleSubmit, setFieldValue }) => (
        <>
          <form
            id="form1"
            className="request-doc-form"
            onSubmit={event => {
              updateFormState(values);
              handleSubmit(event);
            }}
          >
            <Title title={technicalDocSupport} classes="request-doc-title b" />
            <p className="request-doc-subtitle">{useFormToRequest}</p>

            <div className="tech-doc-input-container">
              <div className="request-doc-input-child">
                {getTextInput(facility, values, errors, setFieldValue, 'facility')}
              </div>

              <div className="request-doc-input-child">
                {getTextInput(requestDescription, values, errors, setFieldValue, 'requestDescription')}
              </div>

              <div className="request-doc-input-child">
                <Label className="request-doc-input-label b required" htmlFor="comments">
                  {detailOfRequest}
                </Label>
                <textarea
                  rows="4"
                  placeholder={messagePlaceHolder}
                  value={values.detailOfRequest}
                  onChange={handleChange(setFieldValue)}
                  className={`request-doc-input-field ${errors.detailOfRequest ? 'error' : ''}`}
                  id="detailOfRequest"
                />
                {errors.detailOfRequest && <FormValidationError errorMessage={errors.detailOfRequest} />}
              </div>
            </div>
            <button type="submit" className="hidden" ref={hiddenInnerSubmitFormRef} aria-label="submit" />
          </form>
          <FileUpload
            fileButtonLabel={browseFile}
            dragFileLabel={dragFile}
            requirementMessageOne={requirementMessageOne}
            requirementMessageTwo={requirementMessageTwo}
            orLabel={orLabel}
            onDrop={onFileSelect}
            acceptedExtensions={acceptedExtensions}
            allowMultipleFiles={false}
          />
          <ul className="file-list-container">
            {selectedFiles.map(file => (
              <li className="file-list-item b" key={file.name}>
                {file.name}

                <span
                  className="remove-wrap"
                  role="button"
                  tabIndex="0"
                  onKeyPress={() => onRemoveFile(file)}
                  onClick={() => onRemoveFile(file)}
                  aria-label="remove"
                >
                  <CustomIcon className="b remove-file" type="mdClose" size="14" />
                </span>
              </li>
            ))}
          </ul>
          <div className="button-wrapper">
            <Button type="button" handleClick={handleCloseModal} classes="request-doc-cancel b">
              {cancelLabel}
            </Button>
            <Button classes="request-doc-submit b" handleClick={handleSend}>
              {sendLabel}
            </Button>
          </div>
        </>
      )}
    </Formik>
  );
};

FormRequestInfoPage.propTypes = {
  labels: PropTypes.instanceOf(Object).isRequired,
  messages: PropTypes.instanceOf(Object).isRequired,
  onSubmit: PropTypes.func.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  onFileSelect: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  acceptedExtensions: PropTypes.string,
  selectedFiles: PropTypes.instanceOf(Array)
};

FormRequestInfoPage.defaultProps = {
  acceptedExtensions: '.pdf,.jpg,.png,.xls,.xlsx,.doc,.docx',
  selectedFiles: []
};

export default FormRequestInfoPage;
